import { FC, useCallback, useMemo, useRef, useState } from 'react';

import {
  DashboardRole,
  getDashboardCogsDetail,
  getDashboardSalesDetail,
} from '@shared/api/giftOrder/giftOrder.controller';
import { LowerRole } from '@shared/types';

import { DashboardProfitTab } from '../components/DashboardProfitTab';
import { DashboardSalesDetailTab } from '../components/DashboardSalesDetailTab';
import { DashboardSummaryTab } from '../components/DashboardSummaryTab';
import { DashboardWindfallTab } from '../components/DashboardWindfallTab';
import {
  endOfMonth,
  fmtMonth,
  fmtMonthKo,
  parseMonth,
  startOfMonth,
} from '../utils/dashboard.utils';
import { exportCogsDetailExcel, exportSalesDetailExcel } from '../utils/dashboardExcel';

type DashboardTab = 'summary' | 'profit' | 'windfall' | 'sales';

const TABS: { key: DashboardTab; label: string }[] = [
  { key: 'summary', label: '요약' },
  { key: 'profit', label: '수익 분석' },
  { key: 'sales', label: '매출 상세' },
  { key: 'windfall', label: '낙전 분석' },
];

export const GiftOrderDashboardPage: FC<{ role?: DashboardRole }> =
  function GiftOrderDashboardPage({ role }) {
    const now = new Date();
    const [month, setMonth] = useState(fmtMonth(now));
    const [tab, setTab] = useState<DashboardTab>('summary');
    const monthInputRef = useRef<HTMLInputElement>(null);

    const { from, to } = useMemo(() => {
      const d = parseMonth(month);
      return { from: startOfMonth(d).toISOString(), to: endOfMonth(d).toISOString() };
    }, [month]);

    const handlePrevMonth = () => {
      const d = parseMonth(month);
      d.setMonth(d.getMonth() - 1);
      setMonth(fmtMonth(d));
    };
    const handleNextMonth = () => {
      const d = parseMonth(month);
      d.setMonth(d.getMonth() + 1);
      if (d <= now) setMonth(fmtMonth(d));
    };

    const [excelMenu, setExcelMenu] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);
    const hideReceiver = role === LowerRole.BugoAgencyAdmin;
    const periodLabel = fmtMonthKo(parseMonth(month)).replace(/\s/g, '');

    const handleExportSales = useCallback(async () => {
      setExcelLoading(true);
      setExcelMenu(false);
      try {
        const data = await getDashboardSalesDetail(from, to, role);
        exportSalesDetailExcel(data, periodLabel, hideReceiver);
      } catch {
        alert('매출 상세 데이터를 불러올 수 없습니다.');
      } finally {
        setExcelLoading(false);
      }
    }, [from, to, role, periodLabel, hideReceiver]);

    const handleExportCogs = useCallback(async () => {
      setExcelLoading(true);
      setExcelMenu(false);
      try {
        const data = await getDashboardCogsDetail(from, to, role);
        exportCogsDetailExcel(data, periodLabel, hideReceiver);
      } catch {
        alert('매입 상세 데이터를 불러올 수 없습니다.');
      } finally {
        setExcelLoading(false);
      }
    }, [from, to, role, periodLabel, hideReceiver]);

    return (
      <div className="min-h-screen bg-gray-100 p-6">
        {/* 헤더 */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">수익 대시보드</h1>
            <div className="flex items-center rounded-md bg-gray-200 p-0.5 text-xs font-semibold">
              {TABS.map((t) => (
                <div key={t.key} className="flex items-center">
                  {t.key === 'windfall' && (
                    <span className="mx-1.5 h-4 w-px bg-gray-400/60" />
                  )}
                  <button
                    onClick={() => setTab(t.key)}
                    className={`rounded px-2.5 py-1 transition-colors ${
                      tab === t.key
                        ? t.key === 'windfall'
                          ? 'bg-amber-500 text-white shadow-sm'
                          : 'bg-white text-gray-900 shadow-sm'
                        : t.key === 'windfall'
                        ? 'text-amber-600 hover:text-amber-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t.label}
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {tab !== 'windfall' && (
              <>
                <button
                  onClick={handlePrevMonth}
                  className="rounded-lg bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow hover:bg-gray-50"
                >
                  ◀
                </button>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => monthInputRef.current?.showPicker()}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-50"
                  >
                    {fmtMonthKo(parseMonth(month))}
                  </button>
                  <input
                    ref={monthInputRef}
                    type="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    max={fmtMonth(now)}
                    className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
                    tabIndex={-1}
                  />
                </div>
                <button
                  onClick={handleNextMonth}
                  disabled={fmtMonth(parseMonth(month)) >= fmtMonth(now)}
                  className="rounded-lg bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  ▶
                </button>
              </>
            )}

            {/* 엑셀 다운로드 */}
            <div className="relative ml-2">
              <button
                onClick={() => setExcelMenu((v) => !v)}
                disabled={excelLoading}
                className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white shadow hover:bg-emerald-700 disabled:opacity-50"
              >
                {excelLoading ? '저장 중…' : '엑셀 저장'}
              </button>
              {excelMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setExcelMenu(false)}
                  />
                  <div className="absolute right-0 z-20 mt-1 w-44 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                    <button
                      onClick={handleExportSales}
                      className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    >
                      매출 상세 저장
                    </button>
                    <button
                      onClick={handleExportCogs}
                      className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    >
                      매입 상세 저장
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {tab === 'summary' && <DashboardSummaryTab from={from} to={to} role={role} />}
        {tab === 'profit' && <DashboardProfitTab from={from} to={to} role={role} />}
        {tab === 'windfall' && <DashboardWindfallTab from={from} to={to} role={role} />}
        {tab === 'sales' && <DashboardSalesDetailTab from={from} to={to} role={role} />}
      </div>
    );
  };

export default GiftOrderDashboardPage;
